// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * React hook module for fetching a single purchase order by key.
 * Provides a convenient interface for fetching purchase order details.
 */
import { getPurchaseOrder } from '@/purchase-order/api/get-purchase-order';
import { purchaseOrderDetailQueryKey } from '@/purchase-order/query-keys';
import { useSuspenseQuery } from '@tanstack/react-query';

/**
 * React hook for fetching a single purchase order by key.
 * Uses React Query's suspense mode for data fetching.
 *
 * Features:
 * - Automatic data fetching and caching via React Query
 * - Suspense mode for loading states
 * - Automatic refetch on window focus
 *
 * @param purchaseOrderKey - The unique key of the purchase order to fetch
 *
 * @returns Object containing:
 * - purchaseOrder: The purchase order data
 * - Query status information (error, refetch, etc.)
 *
 * @throws Will suspend while loading data
 * @throws Will throw errors from the API if request fails
 */
export function usePurchaseOrder(purchaseOrderKey: string) {
  const { data, ...query } = useSuspenseQuery({
    queryKey: purchaseOrderDetailQueryKey(purchaseOrderKey),
    queryFn: () => getPurchaseOrder(purchaseOrderKey),
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });

  return { purchaseOrder: data, ...query };
}
